/**
 * Minimum-Attendance Roadmap (Phase 5 — Step 5.15).
 *
 * Rank remaining sessions by penalty consequence to suggest lowest-stakes misses.
 */
import { SessionOverrideType } from '@abs/shared-types';
import { computeEffectiveAbsences } from './budgetCalculator';
import { resolveActiveTier } from './penaltyResolver';
/**
 * Rank remaining sessions by the penalty consequence of missing them.
 * Sessions with exams/presentations rank high (dangerous to miss).
 * Regular lectures with no near-term tier trigger rank low (safer to miss).
 */
export function rankRemainingSessions(sessions, entries, policy) {
    const today = new Date().toISOString().slice(0, 10);
    const futureSessions = sessions.filter((s) => s.date > today);
    const effective = computeEffectiveAbsences(entries, policy);
    const { activeTier, nextTier } = resolveActiveTier(policy.tiers, effective.totalPenaltyAbsences);
    // High-risk override types
    const HIGH_RISK_TYPES = [SessionOverrideType.Exam, SessionOverrideType.Presentation, SessionOverrideType.LabPractical];
    const MEDIUM_RISK_TYPES = [SessionOverrideType.GuestSpeaker];
    return futureSessions.map((session) => {
        const overrideType = session.override?.type ?? null;
        let riskScore = 3; // Default: moderate
        let reason = 'Regular session';
        // Override-based risk
        if (overrideType && HIGH_RISK_TYPES.includes(overrideType)) {
            riskScore = 9;
            reason = `${overrideType} session — very high stakes`;
        }
        else if (overrideType && MEDIUM_RISK_TYPES.includes(overrideType)) {
            riskScore = 6;
            reason = `${overrideType} session — moderate stakes`;
        }
        // Budget-based risk
        if (nextTier && effective.totalPenaltyAbsences >= nextTier.absenceThreshold - 1) {
            riskScore = Math.max(riskScore, 8);
            reason += ' (within 1 absence of next penalty tier)';
        }
        else if (activeTier && !nextTier) {
            riskScore = Math.max(riskScore, 7);
            reason += ' (at maximum penalty tier)';
        }
        // If remaining budget is 1 or 0, everything is high risk
        if (effective.totalPenaltyAbsences >= (policy.attendanceFloor - 1)) {
            riskScore = Math.max(riskScore, 9);
            reason += ' (1 absence from auto-fail)';
        }
        return { date: session.date, overrideType, riskScore: Math.min(riskScore, 10), reason };
    }).sort((a, b) => a.riskScore - b.riskScore); // Sort lowest risk first
}
//# sourceMappingURL=minimumAttendanceRoadmap.js.map